import type { Deliverable, Epic } from './types.js';

/** Columns the roadmap can be sorted by. Product Goals always stay in target-date order. */
export type SortKey = 'name' | 'status' | 'itemId' | 'goal' | 'productArea' | 'type' | 'assignee' | 'pmOwner' | 'reporter' | 'sprint' | 'scopingStatus' | 'scopingDue';
export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  dir: SortDir;
}

export const DEFAULT_SORT: SortState = { key: 'name', dir: 'asc' };

export const SORT_KEYS: readonly SortKey[] = ['name', 'status', 'itemId', 'goal', 'productArea', 'type', 'assignee', 'pmOwner', 'reporter', 'sprint', 'scopingStatus', 'scopingDue'];

/** Lookups the comparators need. Option orders come from the live monday column settings. */
export interface SortContext {
  goalName: (id: string) => string | undefined;
  /** Labels in monday order (e.g. Not started, In progress, Stuck, Done). */
  statusOrder: readonly string[];
  scopingOrder: readonly string[];
  sprintOrder: readonly string[];
}

type Sortable = string | number | null | undefined;

const text = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** Natural order for keys like MON-2 < MON-10. */
const keyNumber = (key: string | null): number | null => {
  const m = /(\d+)\s*$/.exec(key ?? '');
  return m ? Number(m[1]) : null;
};

const rank = (order: readonly string[], v: string | null): number | null => {
  if (!v) return null;
  const i = order.indexOf(v);
  return i < 0 ? order.length : i;
};

function valueOf(d: Deliverable, key: SortKey, ctx: SortContext): Sortable {
  switch (key) {
    case 'name':
      return d.name;
    case 'status':
      return rank(ctx.statusOrder, d.status);
    case 'itemId':
      return keyNumber(d.key);
    case 'goal':
      return d.productGoalIds.map((id) => ctx.goalName(id) ?? '').find(Boolean) ?? null;
    case 'productArea':
      return d.productArea;
    case 'type':
      return d.type;
    case 'assignee':
      return d.assignees[0]?.name ?? null;
    case 'pmOwner':
      return d.pmOwners[0]?.name ?? null;
    case 'reporter':
      return d.reporter?.name ?? null;
    case 'sprint':
      return rank(ctx.sprintOrder, d.sprint);
    case 'scopingStatus':
      return rank(ctx.scopingOrder, d.scopingStatus);
    case 'scopingDue':
      return d.scopingDue;
  }
}

function compareValues(a: Sortable, b: Sortable, dir: SortDir): number {
  const ea = a === null || a === undefined || a === '';
  const eb = b === null || b === undefined || b === '';
  if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1; // blanks last, whichever direction
  const c = typeof a === 'number' && typeof b === 'number' ? a - b : text(String(a), String(b));
  return dir === 'asc' ? c : -c;
}

/** Sorts deliverables by the chosen column; ties fall back to name, then board position (stable). */
export function deliverableComparator(sort: SortState, ctx: SortContext): (a: Deliverable, b: Deliverable) => number {
  return (a, b) =>
    compareValues(valueOf(a, sort.key, ctx), valueOf(b, sort.key, ctx), sort.dir) || text(a.name, b.name) || a.position - b.position;
}

/**
 * Epics are ordered by the same column when it means something for an epic (name, product area, owners);
 * for deliverable-only columns they keep board order so the groups do not jump around.
 */
export function epicComparator(sort: SortState): (a: Epic, b: Epic) => number {
  const value = (e: Epic): Sortable => {
    switch (sort.key) {
      case 'name':
        return e.name;
      case 'productArea':
        return e.productArea;
      case 'assignee':
        return e.rdOwners[0]?.name ?? null;
      case 'pmOwner':
        return e.pmOwners[0]?.name ?? null;
      default:
        return null;
    }
  };
  return (a, b) => compareValues(value(a), value(b), sort.dir) || a.position - b.position || text(a.name, b.name);
}

// ---- URL ("sort=-status" means descending) ------------------------------------------------------------------------

export function sortToParam(s: SortState): string | null {
  if (s.key === DEFAULT_SORT.key && s.dir === DEFAULT_SORT.dir) return null;
  return `${s.dir === 'desc' ? '-' : ''}${s.key}`;
}

export function sortFromParam(raw: string | null): SortState {
  if (!raw) return DEFAULT_SORT;
  const dir: SortDir = raw.startsWith('-') ? 'desc' : 'asc';
  const key = raw.replace(/^-/, '') as SortKey;
  return SORT_KEYS.includes(key) ? { key, dir } : DEFAULT_SORT;
}

/** Click on a column header: new column -> ascending; same column -> descending -> back to the default sort. */
export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return key === DEFAULT_SORT.key ? { key, dir: 'asc' } : DEFAULT_SORT;
}
